class ProductForm extends HTMLElement {
    constructor() {
        super();

        this.form = this.querySelector('form');
        this.addToCartButton = this.querySelector('.add-to-cart');
        this.current_variant_id = this.getAttribute('variant-id');
        this.productHandle = this.getAttribute('product-handle');

        this.variants = JSON.parse(
            this.querySelector('#product-variants-json').textContent
        );

        this.currentVariant = this.variants.find(
            (variant) => variant.id == this.current_variant_id
        );

        // Hidden input holding current variant id
        this.variantIdInput = this.querySelector('[data-variant-id-input]');

        // Main image + thumbnails (global on page; only one product, so OK)
        this.mainImage = document.querySelector('.product-image img');
        this.thumbnailButtons = Array.from(
            document.querySelectorAll('.product-thumbnail')
        );

        // Bind handlers
        this._handleSubmit = this.handleFormSubmit.bind(this);
        this._handleOptionChange = this.updateVariant.bind(this);
        this._handleThumbnailClick = this.onThumbnailClick.bind(this);

        this.glide = null;
        this.initializeThumbnailCarousel();
        
    }

    activateThumbnail(thumb, { scrollCarousel = true } = {}) {
        if (!thumb) return;

        const fullSrc = thumb.getAttribute('data-full');
        if (this.mainImage && fullSrc) {
            this.mainImage.src = fullSrc;
        }

        this.thumbnailButtons.forEach((btn) => btn.classList.remove('is-active'));
        thumb.classList.add('is-active');

        if (scrollCarousel && this.glide) {
            const index = this.thumbnailButtons.indexOf(thumb);
            if (index >= 0) {
                this.glide.go('=' + index);
            }
        }
    }

    syncVariantImage(variant) {
        if (!variant?.featured_image || !this.mainImage) return;

        const featuredId = String(variant.featured_image.id);
        const thumb = this.thumbnailButtons.find(
            (btn) => btn.dataset.imageId === featuredId
        );

        if (thumb) {
            this.activateThumbnail(thumb);
        } else {
            this.mainImage.src =
                variant.featured_image.src + '&width=800';
            this.thumbnailButtons.forEach((btn) =>
                btn.classList.remove('is-active')
            );
        }
    }

    initializeThumbnailCarousel(){
        
        const selector = '#product-thumbnails';
        const wrapper = document.querySelector(selector);

        if (!wrapper || typeof Glide === 'undefined') return;

        const glide = new Glide(selector, {
            type: 'slider',
            perView: 5,       // your requirement: always show 5
            gap: 8,
            bound: true,
            rewind: false
            // if you ever add breakpoints here, this code will still behave
        });

        const dotContainer = wrapper.querySelector('[data-thumb-dots]');
        let dots = [];

        function buildDots() {
            if (!dotContainer) return;

            const slides = wrapper.querySelectorAll('.glide__slide');
            const totalSlides = slides.length;
            const perView = glide.settings.perView; // current effective perView
            const totalPages = Math.max(1, Math.ceil(totalSlides / perView));

            // Remove old dots + listeners
            dotContainer.innerHTML = '';
            dots = [];

            if(totalPages > 5){
                

                for (let page = 0; page <= totalPages; page++) {
                    const dot = document.createElement('button');
                    dot.type = 'button';
                    dot.className = 'thumb-dot';
                    dot.dataset.page = page;

                    dot.addEventListener('click', () => {
                        const perViewNow = glide.settings.perView;
                        const targetIndex = page * perViewNow;
                        glide.go('=' + targetIndex);
                    });

                    dotContainer.appendChild(dot);
                    dots.push(dot);
                }
            }
        }

        function updateDots() {
            if (!dots.length) return;

            const perView = glide.settings.perView;
            const totalPages = dots.length;
            let currentPage = Math.floor(glide.index / perView);

            // Clamp currentPage so it never points past the last dot
            if (currentPage > totalPages - 1) {
                currentPage = totalPages - 1;
            } else if (currentPage < 0) {
                currentPage = 0;
            }

            dots.forEach((dot, i) => {
                dot.classList.toggle('is-active', i === currentPage);
            });
        }

        glide.on('run.after', () => {
            updateDots();
        });

        glide.on('resize', () => {
            buildDots();
            updateDots();
        });


        glide.on('mount.after', () => {
            const thumbnails = document.querySelector('.product-thumbnails');

            if (thumbnails) {
                thumbnails.classList.add('loaded');
            }

            buildDots();
            updateDots();
        });

        glide.on('run.after', updateDots);

        setTimeout(() => {
            glide.update();
            if (this.currentVariant?.featured_image) {
                this.syncVariantImage(this.currentVariant);
            } else {
                glide.go('=0');
            }
        }, 100);

        glide.mount();
        this.glide = glide;
    }

    connectedCallback() {
        this.addEventListener('submit', this._handleSubmit);
        this.addEventListener('input', this._handleOptionChange);

        // Thumbnail click handlers
        this.thumbnailButtons.forEach((btn) => {
            btn.addEventListener('click', this._handleThumbnailClick);
        });
    }

    disconnectedCallback() {
        this.removeEventListener('submit', this._handleSubmit);
        this.removeEventListener('input', this._handleOptionChange);

        this.thumbnailButtons.forEach((btn) => {
            btn.removeEventListener('click', this._handleThumbnailClick);
        });
    }

    onThumbnailClick(event) {
        this.activateThumbnail(event.currentTarget, { scrollCarousel: false });
    }

    updateVariant() {
        // Collect selected options in DOM order (Color, Size, etc.)
        const optionFieldsets = Array.from(
            this.querySelectorAll('.product-option')
        );

        const selectedOptions = optionFieldsets.map((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : null;
        });

        // Not all options selected yet
        if (selectedOptions.some((value) => value === null)) {
            return;
        }

        // Find matching variant
        const matchingVariant = this.variants.find((variant) => {
            if (variant.options.length !== selectedOptions.length) return false;
            return variant.options.every(
                (optionValue, index) => optionValue === selectedOptions[index]
            );
        });

        if (!matchingVariant) {
            console.warn('No matching variant found for options', selectedOptions);
            return;
        }

        this.currentVariant = matchingVariant;

        // Update hidden variant ID input
        if (this.variantIdInput) {
            this.variantIdInput.value = this.currentVariant.id;
        }

        // Update URL
        const newUrl = `/products/${this.productHandle}?variant=${this.currentVariant.id}`;
        window.history.replaceState({}, '', newUrl);

        this.syncVariantImage(this.currentVariant);

        // Update price
        const priceEl = document.querySelector('.product-price');
        if (priceEl) {
            priceEl.innerText = Shopify.formatMoney(this.currentVariant.price);
        }

        // Update button state
        const button = this.querySelector('button[type="submit"]');
        if (button) {
            if (!this.currentVariant.available) {
                button.setAttribute('disabled', '');
                button.textContent = 'Sold out';
            } else {
                button.removeAttribute('disabled');
                button.textContent = 'Add to cart';
            }
        }
    }

    handleFormSubmit(evt) {
        evt.preventDefault();

        const formData = new FormData(this.form);
        document.querySelector('side-cart').classList.remove('loaded');



        fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            body: formData
        })
            .then((response) => response.json())
            .then((json_response) => {
                console.log(json_response);

                document.querySelector('.sidebar-nav').classList.add('open');
                document.querySelector('.sidebar-nav').classList.add('cart');
                document.querySelector('side-cart').classList.add('loaded');

                if (json_response.status != 'bad_request') {
                    document.querySelector('side-cart').addLineItem(json_response);
                } else {
                    alert('Error: ' + json_response.message);
                }
            })
            .catch((error) => {
                alert('Error: ' + error);
                console.error('Error:', error);
            });
    }
}

customElements.define('product-form', ProductForm);
